import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, Plus, Trash2, Edit2, Wand2, Save, Check, X,
  Loader2, ChevronDown, ChevronUp, Zap, Link2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { API, PLATFORM_COLORS } from "./constants";
import { useAuth } from "@/contexts/AuthContext";

const T = {
  ko: {
    title: "아티스트 그룹 관리",
    subtitle: "같은 아티스트의 여러 플랫폼 계정을 그룹으로 연결합니다",
    autoMatch: "자동 매칭",
    autoMatchDesc: "이름 유사도 기반으로 자동 그룹 추천",
    createGroup: "새 그룹",
    groupName: "그룹 이름",
    accounts: "계정",
    platforms: "플랫폼",
    edit: "수정",
    delete: "삭제",
    save: "저장",
    cancel: "취소",
    noGroups: "아직 아티스트 그룹이 없습니다",
    noGroupsDesc: "자동 매칭을 실행하거나 수동으로 그룹을 생성하세요",
    suggestions: "추천 그룹",
    saveAll: "모두 저장",
    saveSuggestion: "저장",
    confidence: "신뢰도",
    matchMethod: "매칭 방법",
    groupField: "기존 그룹 필드",
    nameSimilarity: "이름 유사도",
    noSuggestions: "추천 그룹이 없습니다",
    selectAccounts: "계정 선택",
    followers: "팔로워",
    running: "실행 중...",
    created: "그룹이 생성되었습니다",
    updated: "그룹이 수정되었습니다",
    deleted: "그룹이 삭제되었습니다",
    savedAll: "추천 그룹이 모두 저장되었습니다",
    confirmDelete: "정말 삭제하시겠습니까?",
    spillover: "스필오버 분석",
  },
  en: {
    title: "Artist Group Management",
    subtitle: "Link same artist's accounts across different platforms",
    autoMatch: "Auto Match",
    autoMatchDesc: "Auto-suggest groups based on name similarity",
    createGroup: "New Group",
    groupName: "Group Name",
    accounts: "Accounts",
    platforms: "Platforms",
    edit: "Edit",
    delete: "Delete",
    save: "Save",
    cancel: "Cancel",
    noGroups: "No artist groups yet",
    noGroupsDesc: "Run auto-match or create groups manually",
    suggestions: "Suggested Groups",
    saveAll: "Save All",
    saveSuggestion: "Save",
    confidence: "Confidence",
    matchMethod: "Match Method",
    groupField: "Group Field",
    nameSimilarity: "Name Similarity",
    noSuggestions: "No suggestions available",
    selectAccounts: "Select Accounts",
    followers: "Followers",
    running: "Running...",
    created: "Group created",
    updated: "Group updated",
    deleted: "Group deleted",
    savedAll: "All suggestions saved",
    confirmDelete: "Are you sure you want to delete?",
    spillover: "Spillover Analysis",
  },
};

const PlatformBadge = ({ platform }) => (
  <Badge
    data-testid={`platform-badge-${platform}`}
    variant="outline"
    className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0"
    style={{ borderColor: PLATFORM_COLORS[platform] || "#666", color: PLATFORM_COLORS[platform] || "#aaa" }}
  >
    {platform}
  </Badge>
);

const formatFollowers = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n?.toLocaleString() || "0";
};

export default function ArtistGroupsPage({ lang = "ko" }) {
  const t = T[lang] || T.ko;
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoMatchLoading, setAutoMatchLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [allAccounts, setAllAccounts] = useState([]);

  // Create/edit form state
  const [formName, setFormName] = useState("");
  const [formAccountIds, setFormAccountIds] = useState([]);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/artist-groups`, { headers });
      setGroups(res.data.groups || []);
    } catch (err) {
      console.error("Failed to fetch groups:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/accounts`, { headers });
      setAllAccounts(res.data.accounts || res.data || []);
    } catch (err) {
      console.error("Failed to fetch accounts:", err);
    }
  }, [token]);

  useEffect(() => {
    fetchGroups();
    fetchAccounts();
  }, [fetchGroups, fetchAccounts]);

  const runAutoMatch = async () => {
    setAutoMatchLoading(true);
    try {
      const res = await axios.post(`${API}/artist-groups/auto-match`, null, { headers });
      setSuggestions(res.data.suggestions || []);
      setShowSuggestions(true);
    } catch (err) {
      toast.error("Auto-match failed");
    } finally {
      setAutoMatchLoading(false);
    }
  };

  const saveSuggestion = async (sug) => {
    try {
      await axios.post(`${API}/artist-groups/save-suggestions`, [sug], { headers });
      toast.success(t.created);
      setSuggestions((prev) => prev.filter((s) => s.suggested_name !== sug.suggested_name));
      fetchGroups();
    } catch (err) {
      toast.error("Failed to save group");
    }
  };

  const saveAllSuggestions = async () => {
    try {
      await axios.post(`${API}/artist-groups/save-suggestions`, suggestions, { headers });
      toast.success(t.savedAll);
      setSuggestions([]);
      setShowSuggestions(false);
      fetchGroups();
    } catch (err) {
      toast.error("Failed to save groups");
    }
  };

  const openCreate = () => {
    setFormName("");
    setFormAccountIds([]);
    setEditingGroup(null);
    setShowCreate(true);
  };

  const openEdit = (group) => {
    setFormName(group.name);
    setFormAccountIds(group.account_ids || []);
    setEditingGroup(group);
    setShowCreate(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) return;
    try {
      if (editingGroup) {
        await axios.put(`${API}/artist-groups/${editingGroup.id}`, {
          name: formName,
          account_ids: formAccountIds,
        }, { headers });
        toast.success(t.updated);
      } else {
        await axios.post(`${API}/artist-groups`, {
          name: formName,
          account_ids: formAccountIds,
        }, { headers });
        toast.success(t.created);
      }
      setShowCreate(false);
      fetchGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (groupId) => {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await axios.delete(`${API}/artist-groups/${groupId}`, { headers });
      toast.success(t.deleted);
      fetchGroups();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const toggleAccount = (accountId) => {
    setFormAccountIds((prev) =>
      prev.includes(accountId) ? prev.filter((id) => id !== accountId) : [...prev, accountId]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="artist-groups-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold tracking-tight" data-testid="artist-groups-title">
            <Users className="inline-block mr-2 h-6 w-6 text-primary" />
            {t.title}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t.subtitle}</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="auto-match-btn"
            variant="outline"
            onClick={runAutoMatch}
            disabled={autoMatchLoading}
            className="border-primary/30 hover:border-primary/60"
          >
            {autoMatchLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
            {autoMatchLoading ? t.running : t.autoMatch}
          </Button>
          <Button data-testid="create-group-btn" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            {t.createGroup}
          </Button>
        </div>
      </div>

      {/* Auto-match suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <Card className="border-primary/30 bg-primary/5">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Wand2 className="h-4 w-4 text-primary" />
                {t.suggestions} ({suggestions.length})
              </CardTitle>
              <Button data-testid="save-all-suggestions-btn" size="sm" onClick={saveAllSuggestions}>
                <Save className="h-3 w-3 mr-1" /> {t.saveAll}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {suggestions.map((sug, idx) => (
              <div
                key={idx}
                data-testid={`suggestion-${idx}`}
                className="flex items-center justify-between p-3 rounded-md bg-card border border-border/50"
              >
                <div className="flex-1">
                  <div className="font-medium text-sm">{sug.suggested_name}</div>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {sug.accounts?.map((a) => (
                      <PlatformBadge key={a.id} platform={a.platform} />
                    ))}
                  </div>
                  <div className="text-[10px] text-muted-foreground/70 mt-1 font-mono">
                    {sug.match_method === "group_field" ? t.groupField : t.nameSimilarity}
                    {sug.confidence < 1 && ` · ${t.confidence}: ${Math.round(sug.confidence * 100)}%`}
                  </div>
                </div>
                <Button
                  data-testid={`save-suggestion-${idx}`}
                  size="sm"
                  variant="outline"
                  onClick={() => saveSuggestion(sug)}
                >
                  <Check className="h-3 w-3 mr-1" /> {t.saveSuggestion}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {showSuggestions && suggestions.length === 0 && (
        <Card className="border-muted">
          <CardContent className="py-6 text-center text-sm text-muted-foreground">
            {t.noSuggestions}
          </CardContent>
        </Card>
      )}

      {/* Existing Groups */}
      {groups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
            <p className="font-medium text-muted-foreground">{t.noGroups}</p>
            <p className="text-sm text-muted-foreground/70 mt-1">{t.noGroupsDesc}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {groups.map((group) => (
            <Card key={group.id} data-testid={`group-card-${group.id}`} className="hover:border-primary/20 transition-colors">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-heading">{group.name}</CardTitle>
                  <div className="flex gap-1">
                    <Button
                      data-testid={`edit-group-${group.id}`}
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => openEdit(group)}
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      data-testid={`delete-group-${group.id}`}
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(group.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(group.accounts || []).map((acc) => (
                    <div key={acc.id} className="flex items-center justify-between text-sm py-1">
                      <div className="flex items-center gap-2">
                        <PlatformBadge platform={acc.platform} />
                        <span className="text-foreground">{acc.display_name || acc.username}</span>
                      </div>
                      <span className="text-muted-foreground font-mono text-xs">
                        {formatFollowers(acc.followers || 0)} {t.followers}
                      </span>
                    </div>
                  ))}
                </div>
                {group.auto_matched && (
                  <Badge variant="outline" className="mt-3 text-[10px] text-muted-foreground/60">
                    <Wand2 className="h-2.5 w-2.5 mr-1" /> {t.autoMatch}
                  </Badge>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg" data-testid="group-dialog">
          <DialogHeader>
            <DialogTitle>{editingGroup ? t.edit : t.createGroup}</DialogTitle>
            <DialogDescription>{t.selectAccounts}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">{t.groupName}</label>
              <Input
                data-testid="group-name-input"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. A2O MAY"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">{t.selectAccounts}</label>
              <ScrollArea className="h-64 border rounded-md p-2">
                {allAccounts.map((acc) => (
                  <label
                    key={acc.id}
                    className="flex items-center gap-3 py-2 px-2 rounded hover:bg-muted/50 cursor-pointer"
                    data-testid={`account-option-${acc.id}`}
                  >
                    <Checkbox
                      checked={formAccountIds.includes(acc.id)}
                      onCheckedChange={() => toggleAccount(acc.id)}
                    />
                    <PlatformBadge platform={acc.platform} />
                    <span className="text-sm flex-1">{acc.display_name || acc.username}</span>
                    <span className="text-xs text-muted-foreground font-mono">
                      {formatFollowers(acc.followers || 0)}
                    </span>
                  </label>
                ))}
              </ScrollArea>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              {t.cancel}
            </Button>
            <Button data-testid="save-group-btn" onClick={handleSave} disabled={!formName.trim()}>
              <Save className="h-4 w-4 mr-2" />
              {t.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
